/**
 * Slot-fill assembly engine — Protocol 1.02 narrative builder.
 *
 * Walks the required data-point list (Section 4 stepping algorithm),
 * pulls a sentence template from the reservoir per data point, fills
 * slots from `call_data`, applies bold formatting to EMS-administered
 * medications (§7), assembles paragraphs by format section (§6), and
 * runs the validation checklist (§9).
 *
 * Output is a Markdown-rendered narrative draft. The bold wrapper is
 * `**…**` for clipboard / Markdown ePCRs; HTML and ImageTrend
 * rendering targets transform the same draft via `bold-formatter.ts`.
 */
import { reservoir, pick, type Variants } from './reservoir';
import {
  type CallData,
  type MedicationAdmin,
  type ProcedureRecord,
  type VitalsSet,
  type ProviderLevel,
  RI_CONTROLLED_SUBSTANCES,
  NON_RESTOCK_BOLD_EXCLUSIONS,
  withinScope,
  minScopeForDrug,
  minScopeForProcedure,
} from './schema';
import { selectFormat, type NarrativeFormat } from './format-selector';

export interface AssembledDraft {
  format: NarrativeFormat;
  paragraphs: { letter: string; heading: string; body: string }[];
  fullText: string;
  validation: ValidationResult;
}

export interface ValidationResult {
  ok: boolean;
  failures: string[];
  warnings: string[];
}

// ── Slot-fill primitive ─────────────────────────────────────────
function fill(template: string, slots: Record<string, string>): string {
  return template.replace(/\[([\w.]+)\]/g, (_m, key) => {
    const v = slots[key];
    return v === undefined ? `[${key}]` : v;
  });
}

function vitalsSlots(prefix: 'vitals_set_1' | 'vitals_set_2', v: VitalsSet): Record<string, string> {
  return {
    [`${prefix}.time`]: v.time,
    [`${prefix}.bp`]: v.bp,
    [`${prefix}.pulse`]: v.pulse,
    [`${prefix}.rr`]: v.rr,
    [`${prefix}.spo2`]: v.spo2,
    [`${prefix}.o2`]: v.o2,
  };
}

// ── Bold-formatting (Section 7) ────────────────────────────────
function shouldBoldMedication(m: MedicationAdmin): boolean {
  if (m.excludeFromBold) return false;
  if (m.patientOwn) return false;
  const drug = m.drug.toUpperCase();
  if (NON_RESTOCK_BOLD_EXCLUSIONS.has(drug)) return false;
  return true;
}

function isControlledSubstance(m: MedicationAdmin): boolean {
  if (m.controlledSubstance === false) return false;
  if (m.controlledSubstance === true) return true;
  const drug = m.drug.toUpperCase();
  return RI_CONTROLLED_SUBSTANCES.has(drug);
}

function assembleMedicationSentence(m: MedicationAdmin, seed: number): string {
  const variants = m.patientOwn ? reservoir.med_admin_assisted : reservoir.med_admin;
  const template = pick(variants, seed);
  const slots: Record<string, string> = {
    drug: m.drug,
    dose: m.dose,
    route: m.route,
    time: m.time,
    performer: m.performer,
    response: m.response,
  };
  let sentence = fill(template, slots);
  if (isControlledSubstance(m)) {
    // Append [CS] inside the bolded span per §7 ("Controlled
    // substances: append [CS] after the response sentence for log
    // reconciliation").
    sentence = sentence.trimEnd() + ' [CS]';
  }
  if (shouldBoldMedication(m)) {
    sentence = `**${sentence}**`;
  }
  return sentence;
}

// ── Section builders ────────────────────────────────────────────
function buildDispatch(call: CallData, seed: number): string {
  if (call.dispatch_location && call.dispatch_reason && call.arrival_finding) {
    return fill(pick(reservoir.dispatch.arrival, seed), {
      dispatch_location: call.dispatch_location,
      dispatch_reason: call.dispatch_reason,
      arrival_finding: call.arrival_finding,
    });
  }
  return fill(pick(reservoir.dispatch.arrival_minimal, seed), {
    dispatch_reason: call.dispatch_reason ?? 'an EMS response',
  });
}

function buildComplaint(call: CallData, seed: number): string {
  const variants = call.moi ? reservoir.complaint.trauma : reservoir.complaint.medical;
  return fill(pick(variants, seed), {
    chief_complaint: call.chief_complaint,
    moi: call.moi ?? '',
  });
}

function buildHistory(call: CallData, seed: number): string {
  const out: string[] = [];
  if (call.history_source) {
    out.push(fill(pick(reservoir.history_source_other, seed), { history_source: call.history_source }));
  }
  out.push(fill(pick(reservoir.hpi, seed + 1), { hpi: call.hpi, history_source: call.history_source ?? 'the patient' }));
  if (call.pmh && call.pmh.trim() && !/^none|^no past/i.test(call.pmh)) {
    out.push(fill(pick(reservoir.pmh_present, seed + 2), { pmh: call.pmh }));
  } else {
    out.push(pick(reservoir.pmh_absent, seed + 2));
  }
  if (call.medications && call.medications.trim() && !/^none\b/i.test(call.medications)) {
    out.push(fill(pick(reservoir.meds_present, seed + 3), { medications: call.medications }));
  } else {
    out.push(pick(reservoir.meds_absent, seed + 3));
  }
  if (call.allergies && call.allergies.trim() && !/^nkda\b|^none\b|^no\s+known/i.test(call.allergies)) {
    out.push(fill(pick(reservoir.allergies_present, seed + 4), { allergies: call.allergies }));
  } else {
    out.push(pick(reservoir.allergies_absent, seed + 4));
  }
  if (call.family_social_hx && call.family_social_hx.trim()) {
    out.push(fill(pick(reservoir.family_social_relevant, seed + 5), { family_social_hx: call.family_social_hx }));
  }
  return out.join(' ');
}

function buildAssessment(call: CallData, seed: number): string {
  const out: string[] = [];
  out.push(fill(pick(reservoir.exam, seed), { exam_findings: call.exam_findings }));
  // ECG interpretation gates on AEMT-Cardiac scope (Protocol 7.18).
  // EMT and AEMT operators do not see this slot, and even if a
  // call_data was constructed with ecg_interpretation populated
  // (e.g., a paramedic intercept where an AEMT-C ran the ECG
  // earlier), the narrative attributes it to the recording
  // provider, not to the documenting operator's scope.
  if (call.ecg_interpretation && withinScope(call.provider_level, 'AEMT-Cardiac')) {
    out.push(fill(pick(reservoir.ecg, seed + 1), { ecg_interpretation: call.ecg_interpretation }));
  }
  if (call.bg_value) {
    out.push(fill(pick(reservoir.bg, seed + 2), { bg_value: call.bg_value }));
  }
  if (call.vitals_set_1) {
    out.push(fill(pick(reservoir.vitals_first, seed + 3), vitalsSlots('vitals_set_1', call.vitals_set_1)));
  }
  if (call.vitals_set_2) {
    out.push(fill(pick(reservoir.vitals_repeat, seed + 4), vitalsSlots('vitals_set_2', call.vitals_set_2)));
  }
  out.push(fill(pick(reservoir.field_dx, seed + 5), { field_diagnosis: call.field_diagnosis }));
  if (call.protocols_invoked.length > 0) {
    const list = formatProtocolList(call.protocols_invoked);
    out.push(fill(pick(reservoir.protocols_invoked, seed + 6), { protocols_invoked: list }));
  }
  return out.join(' ');
}

/**
 * Apply the §1.01 scope filter and merge in inline-capture vitals.
 * Returns a chronologically-sorted list of "events" — each is either
 * a medication, procedure, or vitals capture — so the rendered
 * paragraph reads like the call flowed: drug → vitals → drug → vitals.
 */
type InterventionEvent =
  | { kind: 'med'; time: string; med: MedicationAdmin }
  | { kind: 'proc'; time: string; proc: ProcedureRecord }
  | { kind: 'vitals'; time: string; vitals: VitalsSet };

function applyScopeFilter(call: CallData): { meds: MedicationAdmin[]; procs: ProcedureRecord[] } {
  const meds = call.interventions.filter((m) => {
    const required = m.min_scope_level ?? minScopeForDrug(m.drug);
    return withinScope(call.provider_level, required);
  });
  const procs = call.procedures.filter((p) => {
    const required = p.min_scope_level ?? minScopeForProcedure(p.procedure);
    return withinScope(call.provider_level, required);
  });
  return { meds, procs };
}

function renderInlineVitals(v: VitalsSet): string {
  // Standardized format the EHR auto-parser regex-matches against
  // (design-doc §2 Change 2). Anchor: "Vitals at HHMM:".
  const o2 = v.o2 ? ` on ${v.o2}` : '';
  return `Vitals at ${v.time}: BP ${v.bp}, pulse ${v.pulse}, respirations ${v.rr}, SpO2 ${v.spo2} percent${o2}.`;
}

function buildInterventions(call: CallData, seed: number): string {
  const { meds, procs } = applyScopeFilter(call);
  const events: InterventionEvent[] = [
    ...meds.map((med): InterventionEvent => ({ kind: 'med', time: med.time || '0000', med })),
    ...procs.map((proc): InterventionEvent => ({ kind: 'proc', time: proc.time || '0000', proc })),
    ...(call.vitals ?? []).map((vitals): InterventionEvent => ({ kind: 'vitals', time: vitals.time || '0000', vitals })),
  ];
  events.sort((a, b) => a.time.localeCompare(b.time));

  const sentences: string[] = [];
  for (const [i, ev] of events.entries()) {
    if (ev.kind === 'med') {
      sentences.push(assembleMedicationSentence(ev.med, seed + i));
    } else if (ev.kind === 'proc') {
      sentences.push(fill(pick(reservoir.procedure, seed + 100 + i), {
        procedure: ev.proc.procedure,
        procedure_location: ev.proc.location,
        procedure_attempt: ev.proc.attempt,
        procedure_outcome: ev.proc.outcome.startsWith('with') ? ev.proc.outcome : `${ev.proc.outcome}`,
        procedure_performer: ev.proc.performer,
        procedure_time: ev.proc.time,
      }));
    } else {
      sentences.push(renderInlineVitals(ev.vitals));
    }
  }
  if (sentences.length === 0) {
    sentences.push('No medications or procedures were administered during the encounter.');
  }
  return sentences.join(' ');
}

/**
 * Out-of-scope log helper. When the operator enters interventions
 * that exceed the call's provider_level, we emit them as a
 * suppressed-from-narrative warning the validator surfaces — the
 * narrative itself contains nothing about them, preserving the
 * "cannot document care outside scope" guarantee.
 */
function suppressedInterventions(call: CallData): string[] {
  const out: string[] = [];
  for (const m of call.interventions) {
    const required = m.min_scope_level ?? minScopeForDrug(m.drug);
    if (!withinScope(call.provider_level, required)) {
      out.push(`${m.drug} (${required} scope; logged at ${m.time} but not documented in narrative because operator level is ${call.provider_level})`);
    }
  }
  for (const p of call.procedures) {
    const required = p.min_scope_level ?? minScopeForProcedure(p.procedure);
    if (!withinScope(call.provider_level, required)) {
      out.push(`${p.procedure} (${required} scope; logged at ${p.time} but not documented in narrative because operator level is ${call.provider_level})`);
    }
  }
  return out;
}

function buildTransport(call: CallData, seed: number): string {
  const out: string[] = [];
  if (call.destination) {
    out.push(fill(pick(reservoir.transport, seed), {
      destination: call.destination,
      alert_phrase: call.alert_activation ? ` with ${call.alert_activation}` : '',
    }));
  } else if (!call.is_transport) {
    out.push('Patient was not transported by this EMS unit. See Exception section for disposition.');
  } else {
    out.push('Receiving facility was not documented at the time of this draft.');
  }
  if (call.receiving_provider_name && call.receiving_provider_credential && call.handoff_time) {
    out.push(fill(pick(reservoir.handoff, seed + 1), {
      receiving_provider_name: call.receiving_provider_name,
      receiving_provider_credential: call.receiving_provider_credential,
      handoff_time: call.handoff_time,
    }));
  } else if (call.is_transport) {
    out.push('Receiving provider name and credential were not yet documented at the time of this draft and must be added before submission per Protocol 1.02 paragraph 11.');
  }
  return out.join(' ');
}

function buildException(call: CallData, seed: number): string {
  if (call.exceptions && call.exceptions.trim()) {
    return fill(pick(reservoir.exception_present, seed), { exceptions: call.exceptions });
  }
  return pick(reservoir.exception_none, seed);
}

function formatProtocolList(list: string[]): string {
  if (list.length === 0) return '';
  if (list.length === 1) return `Protocol ${list[0]}`;
  if (list.length === 2) return `Protocols ${list[0]} and ${list[1]}`;
  return `Protocols ${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

// ── Per-format assemblers ───────────────────────────────────────
function assembleDCHARTE(call: CallData, seed: number) {
  return [
    { letter: 'D', heading: 'Dispatch',                         body: buildDispatch(call, seed) },
    { letter: 'C', heading: 'Complaint',                        body: buildComplaint(call, seed + 10) },
    { letter: 'H', heading: 'History',                          body: buildHistory(call, seed + 20) },
    { letter: 'A', heading: 'Assessment',                       body: buildAssessment(call, seed + 30) },
    { letter: 'R', heading: 'Rx (Treatment)',                   body: buildInterventions(call, seed + 40) },
    { letter: 'T', heading: 'Transport / Transfer of Care',     body: buildTransport(call, seed + 50) },
    { letter: 'E', heading: 'Exception',                        body: buildException(call, seed + 60) },
  ];
}

function assembleSOAP(call: CallData, seed: number) {
  const subjective = [
    buildComplaint(call, seed),
    buildHistory(call, seed + 10),
  ].join(' ');
  const objective = buildAssessmentObjectiveOnly(call, seed + 20);
  const assessment = [
    fill(pick(reservoir.field_dx, seed + 30), { field_diagnosis: call.field_diagnosis }),
    call.protocols_invoked.length > 0
      ? fill(pick(reservoir.protocols_invoked, seed + 31), { protocols_invoked: formatProtocolList(call.protocols_invoked) })
      : null,
  ].filter(Boolean).join(' ');
  const plan = [
    buildInterventions(call, seed + 40),
    buildTransport(call, seed + 50),
  ].join(' ');
  return [
    { letter: 'S', heading: 'Subjective', body: subjective },
    { letter: 'O', heading: 'Objective',  body: objective },
    { letter: 'A', heading: 'Assessment', body: assessment },
    { letter: 'P', heading: 'Plan',       body: plan },
  ];
}

function buildAssessmentObjectiveOnly(call: CallData, seed: number): string {
  const out: string[] = [];
  out.push(fill(pick(reservoir.exam, seed), { exam_findings: call.exam_findings }));
  if (call.vitals_set_1) {
    out.push(fill(pick(reservoir.vitals_first, seed + 1), vitalsSlots('vitals_set_1', call.vitals_set_1)));
  }
  if (call.vitals_set_2) {
    out.push(fill(pick(reservoir.vitals_repeat, seed + 2), vitalsSlots('vitals_set_2', call.vitals_set_2)));
  }
  if (call.ecg_interpretation) {
    out.push(fill(pick(reservoir.ecg, seed + 3), { ecg_interpretation: call.ecg_interpretation }));
  }
  if (call.bg_value) {
    out.push(fill(pick(reservoir.bg, seed + 4), { bg_value: call.bg_value }));
  }
  return out.join(' ');
}

function assembleSBAR(call: CallData, seed: number) {
  const situation = call.patient_line && call.reason_for_transport && call.current_status
    ? fill(pick(reservoir.sbar_situation, seed), {
        patient_line: call.patient_line,
        reason_for_transport: call.reason_for_transport,
        current_status: call.current_status,
      })
    : `The patient is being transferred to ${call.destination ?? 'the receiving facility'} for ongoing care.`;
  const background = buildHistory(call, seed + 10) + ' ' + buildAssessmentObjectiveOnly(call, seed + 20);
  const assessment = [
    fill(pick(reservoir.field_dx, seed + 30), { field_diagnosis: call.field_diagnosis }),
    buildInterventions(call, seed + 40),
  ].join(' ');
  const recommendation = [
    buildTransport(call, seed + 50),
    call.specific_ask ? fill(pick(reservoir.sbar_recommendation, seed + 60), { specific_ask: call.specific_ask }) : null,
  ].filter(Boolean).join(' ');
  return [
    { letter: 'S', heading: 'Situation',      body: situation },
    { letter: 'B', heading: 'Background',     body: background },
    { letter: 'A', heading: 'Assessment',     body: assessment },
    { letter: 'R', heading: 'Recommendation', body: recommendation },
  ];
}

function assembleDISPATCH(call: CallData, seed: number) {
  return [
    { letter: 'D', heading: 'Dispatch',                          body: buildDispatch(call, seed) },
    { letter: 'I', heading: 'Initial Impression',                body: call.arrival_finding
      ? `On arrival, the crew encountered ${call.arrival_finding}.`
      : 'Initial impression on arrival was consistent with the dispatch reason.' },
    { letter: 'S', heading: 'Scene',                             body: call.scene_complexity
      ? 'Scene dynamics required additional consideration; refer to the Exception section.'
      : 'Scene was secure and access to the patient was unobstructed.' },
    { letter: 'P', heading: 'Patient',                           body: buildComplaint(call, seed + 10) + ' ' + buildHistory(call, seed + 20) },
    { letter: 'A', heading: 'Assessment',                        body: buildAssessment(call, seed + 30) },
    { letter: 'T', heading: 'Treatment',                         body: buildInterventions(call, seed + 40) },
    { letter: 'C', heading: 'Changes During Transport',          body: call.status_change
      ? 'Status changed during transport; see Exception section for details.'
      : 'No significant change in patient status was observed during transport.' },
    { letter: 'H', heading: 'Hand-off',                          body: buildTransport(call, seed + 50) },
  ];
}

// ── Public API ──────────────────────────────────────────────────
export function assembleNarrative(call: CallData, seed?: number): AssembledDraft {
  const s = seed ?? Math.floor(Date.now() / 86_400_000); // day-stable variant cycling
  const format = selectFormat(call);
  const paragraphs =
    format === 'DCHARTE'  ? assembleDCHARTE(call, s) :
    format === 'SOAP'     ? assembleSOAP(call, s) :
    format === 'SBAR'     ? assembleSBAR(call, s) :
                            assembleDISPATCH(call, s);

  const fullText = renderFullText(format, paragraphs, call);
  const validation = validate(fullText, call, format);
  return { format, paragraphs, fullText, validation };
}

function renderFullText(
  format: NarrativeFormat,
  paragraphs: { letter: string; heading: string; body: string }[],
  call: CallData,
): string {
  const generatedAt = new Date().toISOString();
  return [
    '═══════════════════════════════════════════════════════════',
    '⚠ DRAFT — DO NOT POST WITHOUT LICENSED-CLINICIAN REVIEW ⚠',
    '═══════════════════════════════════════════════════════════',
    `Format: ${format}  ·  Source: RI Statewide EMS Protocols and Standing Orders v2026.01, §1.02`,
    `Generated: ${generatedAt}`,
    '',
    '─── NARRATIVE BEGINS ───',
    '',
    ...paragraphs.map((p) => `${p.letter}: ${p.body}`),
    '',
    '─── NARRATIVE ENDS ───',
    '',
    `Receiving provider: ${call.receiving_provider_name ?? '____________'}, ${call.receiving_provider_credential ?? '____'}`,
    `Reviewed and approved by (clinician name + cert): ____________________`,
    `Signature: ____________________`,
    `Date / time of approval: ____________________`,
  ].join('\n');
}

// ── Validator (Section 9) ───────────────────────────────────────
const BANNED_PATTERNS: { re: RegExp; label: string }[] = [
  // Em / en dashes — Section 2 style rule + project convention.
  { re: /—/, label: 'em dash (—) in narrative body — replace with period or comma' },
  // The ASCII en-dash is OK in dose ranges like "2.5-5 mg" but
  // the unicode en-dash (–) is rejected.
  { re: /–/, label: 'en dash (–) in narrative body — replace with period, comma, or hyphen' },
  // "WNL" — Protocol 1.02 ¶9 explicitly prohibits this in lieu of
  // measured vitals.
  { re: /\bWNL\b/, label: 'WNL is prohibited per Protocol 1.02 ¶9 — document measured vitals' },
  // First person.
  { re: /\b(I|me|my|mine|we|us|our|ours)\b/, label: 'first-person pronoun — Section 2 style requires third person past tense' },
];

export function validate(narrative: string, call: CallData, format: NarrativeFormat): ValidationResult {
  const failures: string[] = [];
  const warnings: string[] = [];

  // We strip the DRAFT header / signature footer before scanning so
  // the boilerplate doesn't trigger false positives on punctuation.
  const begin = narrative.indexOf('─── NARRATIVE BEGINS ───');
  const end = narrative.indexOf('─── NARRATIVE ENDS ───');
  const body = begin >= 0 && end >= 0
    ? narrative.slice(begin + '─── NARRATIVE BEGINS ───'.length, end)
    : narrative;

  for (const { re, label } of BANNED_PATTERNS) {
    if (re.test(body)) failures.push(label);
  }

  // Required data points
  if (!call.chief_complaint?.trim()) failures.push('Protocol 1.02 ¶6.1 — chief_complaint is missing');
  if (!call.hpi?.trim())              failures.push('Protocol 1.02 ¶6.4 — hpi is missing');
  if (call.pmh == null)               warnings.push('Protocol 1.02 ¶6.5 — pmh not addressed (use "none reported" if absent)');
  if (call.medications == null)       warnings.push('Protocol 1.02 ¶6.7 — medications not addressed (use "none" if absent)');
  if (call.allergies == null)         warnings.push('Protocol 1.02 ¶6.8 — allergies not addressed (use "NKDA" if absent)');
  if (!call.exam_findings?.trim())    failures.push('Protocol 1.02 ¶6.9 — exam_findings is missing');
  if (!call.field_diagnosis?.trim())  failures.push('Protocol 1.02 ¶6.11 — field_diagnosis is missing');

  // ¶9 — minimum vitals
  if (call.is_transport) {
    if (!call.vitals_set_1) failures.push('Protocol 1.02 ¶9 — first vitals set is missing on transport');
    if (!call.vitals_set_2) failures.push('Protocol 1.02 ¶9 — second vitals set is missing on transport');
  } else if (!call.vitals_set_1) {
    failures.push('Protocol 1.02 ¶9 — at least one vitals set is required for non-transport contacts');
  }

  // ECG upload reference when ECG was interpreted
  if (call.ecg_interpretation && !/uploaded\s+to\s+the\s+ePCR/i.test(body)) {
    warnings.push('Protocol 1.02 ¶10 — ECG interpretation present but tracing-upload reference not detected');
  }

  // ¶11 — hand-off
  if (call.is_transport) {
    if (!call.receiving_provider_name) failures.push('Protocol 1.02 ¶11 — receiving_provider_name is missing');
    if (!call.receiving_provider_credential) failures.push('Protocol 1.02 ¶11 — receiving_provider_credential is missing');
  }

  // Per-medication completeness (¶8: name / dose / route / time / performer / effect)
  // — only enforced for interventions WITHIN scope; suppressed
  // interventions are surfaced as warnings so the operator knows
  // they were dropped.
  const suppressed = suppressedInterventions(call);
  for (const s of suppressed) warnings.push(`Out-of-scope intervention suppressed: ${s}`);
  for (const [i, m] of call.interventions.entries()) {
    const required = m.min_scope_level ?? minScopeForDrug(m.drug);
    if (!withinScope(call.provider_level, required)) continue;
    const ctx = `interventions[${i}] (${m.drug || 'unnamed'})`;
    if (!m.drug) failures.push(`${ctx}: drug name missing`);
    if (!m.dose) failures.push(`${ctx}: dose missing`);
    if (!m.route) failures.push(`${ctx}: route missing`);
    if (!m.time) failures.push(`${ctx}: time missing`);
    if (!m.performer) failures.push(`${ctx}: administering professional missing`);
    if (!m.response) warnings.push(`${ctx}: effect / response not documented`);
  }
  // Per-procedure completeness (¶7) — within scope only.
  for (const [i, p] of call.procedures.entries()) {
    const required = p.min_scope_level ?? minScopeForProcedure(p.procedure);
    if (!withinScope(call.provider_level, required)) continue;
    const ctx = `procedures[${i}] (${p.procedure || 'unnamed'})`;
    if (!p.location) failures.push(`${ctx}: anatomic location missing`);
    if (!p.attempt) failures.push(`${ctx}: attempt count missing`);
    if (!p.outcome) failures.push(`${ctx}: success / outcome missing`);
    if (!p.performer) failures.push(`${ctx}: performing professional missing`);
    if (!p.time) failures.push(`${ctx}: time missing`);
  }

  // Bold-formatting confirmation — only for interventions actually
  // rendered (within scope). Suppressed interventions don't appear
  // in the narrative so the bold-wrap check would never match.
  for (const m of call.interventions) {
    const required = m.min_scope_level ?? minScopeForDrug(m.drug);
    if (!withinScope(call.provider_level, required)) continue;
    if (shouldBoldMedication(m)) {
      const re = new RegExp(`\\*\\*[^*]*${escapeRegex(m.drug)}[^*]*\\*\\*`, 'i');
      if (!re.test(body)) {
        failures.push(`Section 7 — medication "${m.drug}" not wrapped in bold for restock workflow`);
      }
    }
  }

  return { ok: failures.length === 0, failures, warnings };
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
